<template>
  <div class="relative px-8 pb-8">
    <div class="relative">
      <!-- Carousel Container -->
      <div ref="carouselContainer" class="w-full overflow-x-hidden">
        <div
          ref="carouselTrack"
          class="flex gap-6 carousel-track"
          :style="{ transform: `translateX(-${currentOffset}px)` }"
        >
          <div
            v-for="product in products"
            :key="product.id"
            class="w-[280px] flex-shrink-0"
          >
            <FrontendProductCardSelector
              :product="product"
              class="h-full"
              @add-to-cart="$emit('add-to-cart', $event)"
              @toggle-favorite="$emit('toggle-favorite', $event)"
            />
          </div>
        </div>
      </div>

      <!-- Navigation Buttons -->
      <UButton
        v-show="canScrollPrev"
        @click="scrollPrev"
        color="white"
        class="absolute -left-3 top-1/2 -translate-y-1/2 !w-10 !h-10 !p-0 rounded-full shadow-lg !border-slate-200/50 z-10"
      >
        <UIcon name="i-heroicons-chevron-left" class="w-5 h-5 text-slate-600" />
      </UButton>
      <UButton
        v-show="canScrollNext"
        @click="scrollNext"
        color="white"
        class="absolute -right-3 top-1/2 -translate-y-1/2 !w-10 !h-10 !p-0 rounded-full shadow-lg !border-slate-200/50 z-10"
      >
        <UIcon
          name="i-heroicons-chevron-right"
          class="w-5 h-5 text-slate-600"
        />
      </UButton>
    </div>
  </div>
</template>

<script setup>
const props = defineProps({
  products: {
    type: Array,
    required: true
  }
})

defineEmits(['add-to-cart', 'toggle-favorite'])

// Carousel refs ve state
const carouselContainer = ref(null)
const carouselTrack = ref(null)
const currentOffset = ref(0)
const canScrollPrev = ref(false)
const canScrollNext = ref(false)

// Carousel kontrolleri
const updateScrollButtons = () => {
  if (!carouselContainer.value || !carouselTrack.value) return

  const container = carouselContainer.value
  const track = carouselTrack.value
  const totalWidth = track.scrollWidth
  const visibleWidth = container.clientWidth

  canScrollNext.value =
    Math.ceil(currentOffset.value + visibleWidth) < totalWidth
  canScrollPrev.value = currentOffset.value > 0
}

const scrollNext = () => {
  if (!carouselContainer.value || !carouselTrack.value) return

  const container = carouselContainer.value
  const track = carouselTrack.value
  const itemWidth = 280 + 24
  const itemsPerScreen = Math.floor(container.clientWidth / itemWidth)
  const scrollAmount = itemWidth * itemsPerScreen

  currentOffset.value = Math.min(
    currentOffset.value + scrollAmount,
    track.scrollWidth - container.clientWidth
  )
  updateScrollButtons()
}

const scrollPrev = () => {
  if (!carouselContainer.value || !carouselTrack.value) return

  const container = carouselContainer.value
  const itemWidth = 280 + 24
  const itemsPerScreen = Math.floor(container.clientWidth / itemWidth)
  const scrollAmount = itemWidth * itemsPerScreen

  currentOffset.value = Math.max(currentOffset.value - scrollAmount, 0)
  updateScrollButtons()
}

// Products değiştiğinde scroll durumunu güncelle
watch(
  () => props.products,
  () => {
    nextTick(() => {
      currentOffset.value = 0
      updateScrollButtons()
    })
  }
)

// Resize observer
let resizeObserver
onMounted(() => {
  if (carouselContainer.value) {
    resizeObserver = new ResizeObserver(() => {
      currentOffset.value = Math.min(
        currentOffset.value,
        carouselTrack.value?.scrollWidth -
          carouselContainer.value?.clientWidth || 0
      )
      updateScrollButtons()
    })

    resizeObserver.observe(carouselContainer.value)
  }
})

onUnmounted(() => {
  if (resizeObserver) {
    resizeObserver.disconnect()
  }
})
</script>

<style scoped>
.carousel-track {
  transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}
</style>
